import * as React from 'react';

export interface ComposerTextRequest {
  text: string;
  submit: boolean;
  shouldFocus: boolean;
}

export interface ParentComposerTextRequest extends ComposerTextRequest {
  isNewConversation: boolean;
}

interface UseParentComposerTextOptions {
  thread: { id: string; status: string };
  getMessageCount: () => number;
  /** Typed void by assistant-ui, but the client's switch is async and can reject. */
  switchToNewThread: () => unknown;
  applyComposerText: (request: ComposerTextRequest) => void;
}

/**
 * Applies text the host page sent, first switching to a new thread when it asked for a new
 * conversation. Must be called after useSyncedComposer: on the switch's render its effect has to
 * run after the draft restore, which would otherwise wipe the text.
 */
export function useParentComposerText({
  thread,
  getMessageCount,
  switchToNewThread,
  applyComposerText,
}: UseParentComposerTextOptions): (request: ParentComposerTextRequest) => void {
  // Held across a thread switch: useSyncedComposer loads the new thread's (empty) saved draft
  // into the composer when the switch renders, so text set any earlier is wiped.
  const textForNewThreadRef = React.useRef<(ComposerTextRequest & { fromThreadId: string }) | null>(
    null,
  );

  const applyParentComposerText = React.useCallback(
    function applyParentComposerText({ isNewConversation, ...request }: ParentComposerTextRequest) {
      // Only a thread never sent is already new. A restored one can read empty while its history
      // loads, and sending there would extend the old conversation.
      const isAlreadyNew = thread.status === 'new' && getMessageCount() === 0;
      if (!isNewConversation || isAlreadyNew) {
        // A newer request wins over one still waiting for its thread.
        textForNewThreadRef.current = null;
        applyComposerText(request);
        return;
      }
      const held = { ...request, fromThreadId: thread.id };
      textForNewThreadRef.current = held;
      Promise.resolve(switchToNewThread()).catch(function dropHeldText() {
        // Otherwise it would send into whichever thread the visitor opens next.
        if (textForNewThreadRef.current === held) textForNewThreadRef.current = null;
      });
    },
    [thread.id, thread.status, getMessageCount, switchToNewThread, applyComposerText],
  );

  React.useEffect(
    function applyHeldText() {
      const held = textForNewThreadRef.current;
      if (!held || held.fromThreadId === thread.id) return;
      textForNewThreadRef.current = null;
      // The visitor switched to an existing thread before the new one arrived.
      if (thread.status !== 'new') return;
      const { fromThreadId: _, ...request } = held;
      applyComposerText(request);
    },
    [thread.id, thread.status, applyComposerText],
  );

  return applyParentComposerText;
}
